import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setProducts, 
  setCategories, 
  setSelectedCategory, 
  setSearchTerm, 
  setLoading, 
  setError,
  selectProducts,
  selectAllProducts,
  selectSelectedCategory,
  selectSearchTerm,
  selectProductsLoading,
  selectProductsError,
  selectProductCategories
} from '../services/reducers/products';
import { storeService } from '../services/api/store/storeApi';

export const useStore = () => {
  const dispatch = useDispatch();
  
  // Selectors
  const products = useSelector(selectProducts);
  const allProducts = useSelector(selectAllProducts);
  const selectedCategory = useSelector(selectSelectedCategory);
  const searchTerm = useSelector(selectSearchTerm);
  const loading = useSelector(selectProductsLoading);
  const error = useSelector(selectProductsError);
  const categories = useSelector(selectProductCategories);
  
  // Estado local para paginação NORMAL
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Actions
  const setCategory = useCallback((category) => {
    dispatch(setSelectedCategory(category));
  }, [dispatch]);

  const setSearch = useCallback((search) => {
    dispatch(setSearchTerm(search));
  }, [dispatch]);

  // Carregar produtos da loja - PAGINAÇÃO NORMAL
  const loadProducts = useCallback(async (page = 1) => {
    dispatch(setLoading(true));
    
    try {
      // Fallback de userId quando não houver Authorization (para excluir meus produtos)
      let userIdFallback = null;
      try {
        const u = JSON.parse(localStorage.getItem('user_data') || localStorage.getItem('user') || 'null');
        if (u && (u.id || u.userId)) userIdFallback = u.id || u.userId;
      } catch (_) {}

      const response = await storeService.getProducts({
        category: selectedCategory,
        search: searchTerm,
        page: page,
        limit: 12,
        userId: userIdFallback,
        exclude_my: true
      });
      
      if (response.success) {
        // Sempre substituir produtos na paginação normal
        dispatch(setProducts(response.products));
        dispatch(setCategories(response.categories));
        
        // Atualizar paginação
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar produtos');
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      dispatch(setError(err.message || 'Erro ao carregar produtos da loja'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, selectedCategory, searchTerm]);

  // Navegar para página anterior
  const goToPrevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      loadProducts(pagination.page - 1);
    }
  }, [pagination.hasPrevPage, pagination.page, loadProducts]);

  // Navegar para próxima página
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      loadProducts(pagination.page + 1);
    }
  }, [pagination.hasNextPage, pagination.page, loadProducts]);

  // Ir para página específica
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      loadProducts(page);
    }
  }, [pagination.totalPages, loadProducts]);

  // Carregar produtos apenas na montagem inicial
  useEffect(() => {
    loadProducts(1);
  }, []);

  // Buscar produtos com filtros específicos
  const searchProducts = useCallback(async (filters = {}) => {
    dispatch(setLoading(true));
    
    try {
      const response = await storeService.getProducts(filters);
      
      if (response.success) {
        dispatch(setProducts(response.products));
        dispatch(setCategories(response.categories));
      } else {
        throw new Error(response.message || 'Erro ao buscar produtos');
      }
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      dispatch(setError(err.message || 'Erro ao buscar produtos'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  // Executar seed da loja (apenas para desenvolvimento)
  const seedStore = useCallback(async () => {
    dispatch(setLoading(true));
    
    try {
      const response = await storeService.seedStore();
      
      if (response.success) {
        console.log('Seed executado com sucesso:', response.message);
        await loadProducts(1);
        return response;
      } else {
        throw new Error(response.message || 'Erro ao executar seed');
      }
    } catch (err) {
      console.error('Erro ao executar seed:', err);
      dispatch(setError(err.message || 'Erro ao executar seed da loja'));
      throw err;
    }
  }, [dispatch, loadProducts]);

  return {
    // Estado
    products,
    allProducts,
    selectedCategory,
    searchTerm,
    loading,
    error,
    categories,
    pagination,
    
    // Actions
    setCategory,
    setSearch,
    loadProducts,
    searchProducts,
    goToPrevPage,
    goToNextPage,
    goToPage,
    seedStore
  };
};

export default useStore;